import { forwardRef } from 'react';
import type { MouseEvent } from 'react';

import type { DataExplorerButtonProps } from './types';
import { useDataExplorerAppearance } from './appearanceContext';
import {
  getDataExplorerActionToneClassName,
  resolveDataExplorerAppearance,
} from './theme';
import { cn } from './utils';

function LoadingIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4 animate-spin"
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        className="opacity-25"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0-6-6V3Z"
      />
    </svg>
  );
}

export const DataExplorerButton = forwardRef<
  HTMLButtonElement,
  DataExplorerButtonProps
>(function DataExplorerButton(
  {
    children,
    label,
    icon,
    endIcon,
    type = 'button',
    title,
    ariaLabel,
    tone,
    active = false,
    iconOnly = false,
    disabled = false,
    loading = false,
    stopPropagation = false,
    appearance,
    className,
    classNames,
    style,
    onClick,
    'aria-label': nativeAriaLabel,
    ...buttonProps
  },
  ref,
) {
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const resolvedLabel = children ?? label;
  const resolvedDisabled = disabled || loading;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (resolvedDisabled) return;
    onClick?.(event);
  };

  return (
    <button
      {...buttonProps}
      ref={ref}
      type={type}
      title={title}
      aria-label={ariaLabel ?? nativeAriaLabel}
      aria-pressed={active || undefined}
      aria-busy={loading || undefined}
      disabled={resolvedDisabled}
      className={cn(
        theme.slots.controlButton,
        iconOnly && theme.slots.actionIconButton,
        getDataExplorerActionToneClassName(theme, tone, active),
        resolvedDisabled && theme.slots.actionButtonDisabled,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.control,
        ...(active ? theme.styles.controlActive : undefined),
        ...(resolvedDisabled ? theme.styles.controlDisabled : undefined),
        ...style,
      }}
      onClick={handleClick}
    >
      {loading ? (
        <span className={cn(theme.slots.controlButtonIcon, classNames?.icon)}>
          <LoadingIcon />
        </span>
      ) : icon ? (
        <span className={cn(theme.slots.controlButtonIcon, classNames?.icon)}>
          {icon}
        </span>
      ) : null}
      {iconOnly ? (
        <span className="sr-only">{resolvedLabel}</span>
      ) : resolvedLabel ? (
        <span className={cn(theme.slots.controlButtonLabel, classNames?.label)}>
          {resolvedLabel}
        </span>
      ) : null}
      {endIcon ? (
        <span className={cn(theme.slots.controlButtonIcon, classNames?.icon)}>
          {endIcon}
        </span>
      ) : null}
    </button>
  );
});
